/**
 * Citadel — Templates Moderation surface.
 *
 * The second non-feature-flag section of Citadel (BL-29-1828).
 * Operationalises the three-layer governance hierarchy (Cybernetic Organism
 * Architecture v2.0 §10): Noeura imagines → Citadel ratifies → Eidolon
 * recommends → user consumes.
 *
 * Phase 1 scaffold per §3.4 of the sprint plan:
 *   - Moderation queue (draft templates pending review)
 *   - Published list (approved, live catalogue)
 *   - Archived list (rejected / retired — read-only audit trail)
 *
 * Visual identity: terminal aesthetic per Citadel_Architecture_ADR.md —
 * JetBrains Mono labels, dense ops dashboard, reads like Datadog / Grafana.
 * NOT customer-facing chrome.
 *
 * Auth model (NEW-P0-A + F4 binding):
 *   Route is reachable only through Cloudflare Access (staff allowlist).
 *   The citadel-ui does NOT inspect Auth0 claims for route guarding.
 *   The page handles a 403 from the BE by rendering a clean Access-denied
 *   state — CFAccess + the BE perm("operations:read") gate is the contract.
 *
 * BVA copy: all user-visible strings are verbatim from
 *   .system/handoff/SPR-26.N-1_BrandVoice_TemplateMarketplace_Ratification_2026-05-13.md
 *   §4.5 Wright-consumable string tables. DO NOT paraphrase.
 *
 * Sprint: SPR-26.N-1 | Task: N4 | Role: Frontend Developer
 * Backlog: BL-29-1820 · BL-29-1828 · BL-29-1834 (Phase 1 scaffold)
 */
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
// Synthesis XII-5: Phosphor (thin) for prominent surface chrome (size-4),
// Heroicons micro (16/solid) for dense in-row badges + spinners (size-3, size-3.5, size-2.5).
import {
    Warning as AlertTriangle,
    ClipboardText as ClipboardList,
    ShieldWarning as ShieldAlert,
} from 'phosphor-react';
import {
    ExclamationTriangleIcon as MicroAlertTriangle,
    CheckCircleIcon as MicroCheckCircle,
    ArrowPathIcon as MicroSpinner,
} from '@heroicons/react/16/solid';
import {
    type CadminTemplate,
    CitadelApiError,
    approveTemplate,
    listModerationQueue,
    listPublished,
    rejectTemplate,
} from '@/lib/api';
import { useAccessTokenSync } from '@/lib/auth';

// ── Types ─────────────────────────────────────────────────────────────────────

/** The three tabs of the Templates Moderation surface. */
type Tab = 'queue' | 'published' | 'archived';

/** Per-row action state to prevent double-submissions. */
interface RowAction {
    pending: boolean;
    error: string | null;
}

// ── Origin badge ──────────────────────────────────────────────────────────────

/** BVA §4.5 — canonical origin badge labels. */
const ORIGIN_LABELS: Record<CadminTemplate['origin'], string> = {
    noeura: 'Noeura',
    user: 'User-submitted',
    eidolon: 'Eidolon',
    adhoc: 'Ad hoc',
};

/** Origin badge colours keyed by origin. */
const ORIGIN_BADGE_CLASSES: Record<CadminTemplate['origin'], string> = {
    noeura: 'bg-citadel-cyan/10 text-citadel-cyan',
    user: 'bg-citadel-indigo/10 text-citadel-ink',
    eidolon: 'bg-citadel-violet/10 text-citadel-ink',
    adhoc: 'bg-citadel-border text-citadel-ink-faint',
};

// ── Relative time helper ──────────────────────────────────────────────────────

function relativeAge(isoString: string): string {
    const ms = Date.now() - new Date(isoString).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
}

// ── Inline subcomponents ──────────────────────────────────────────────────────

interface OriginBadgeProps {
    origin: CadminTemplate['origin'];
}

const OriginBadge = ({ origin }: OriginBadgeProps) => (
    <span
        className={`inline-flex items-center px-1.5 py-0.5 text-2xs font-mono uppercase tracking-wider ${ORIGIN_BADGE_CLASSES[origin] ?? 'bg-citadel-border text-citadel-ink-faint'}`}
    >
        {ORIGIN_LABELS[origin] ?? origin}
    </span>
);

// ── RejectionReasonModal ──────────────────────────────────────────────────────

interface RejectionReasonModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: (reason: string) => void;
    pending: boolean;
}

/** BVA §4.5 — rejection reason modal with required text input. */
const RejectionReasonModal = ({
    open,
    onOpenChange,
    onConfirm,
    pending,
}: RejectionReasonModalProps) => {
    const [reason, setReason] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Reset reason when modal opens.
    useEffect(() => {
        if (open) {
            setReason('');
            // Focus textarea on next tick to allow DOM to settle.
            setTimeout(() => textareaRef.current?.focus(), 0);
        }
    }, [open]);

    const handleConfirm = () => {
        if (!reason.trim() || pending) return;
        onConfirm(reason.trim());
    };

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/60 z-40" />
                <Dialog.Content
                    className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md border border-citadel-border bg-citadel-surface p-5 font-mono shadow-xl"
                    aria-describedby="rejection-modal-description"
                >
                    {/* BVA §4.5: modal title */}
                    <Dialog.Title className="text-sm font-semibold text-citadel-ink mb-1">
                        Reject template
                    </Dialog.Title>
                    <p
                        id="rejection-modal-description"
                        className="text-xs text-citadel-ink-dim mb-4 leading-relaxed"
                    >
                        {/* BVA §4.5: textarea label */}
                        Reason for rejection
                    </p>

                    {/* BVA §4.5: textarea placeholder */}
                    <textarea
                        ref={textareaRef}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="e.g. Policy violation — references restricted category. Safety gate failed."
                        rows={4}
                        className="w-full bg-citadel-bg border border-citadel-border text-xs font-mono text-citadel-ink placeholder:text-citadel-ink-faint p-2 resize-none focus:outline-none focus:border-citadel-cyan/60"
                        aria-label="Reason for rejection"
                        data-testid="rejection-modal-textarea"
                    />

                    <div className="flex items-center justify-end gap-2 mt-4">
                        {/* BVA §4.5: cancel button */}
                        <Dialog.Close asChild>
                            <button
                                type="button"
                                className="px-3 py-1.5 text-xs font-mono text-citadel-ink-dim border border-citadel-border hover:text-citadel-ink hover:border-citadel-border-strong"
                                data-testid="rejection-modal-cancel"
                            >
                                Cancel
                            </button>
                        </Dialog.Close>

                        {/* BVA §4.5: confirm button — disabled when reason empty */}
                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={!reason.trim() || pending}
                            className="px-3 py-1.5 text-xs font-mono bg-citadel-danger/10 border border-citadel-danger/40 text-citadel-danger hover:bg-citadel-danger/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                            data-testid="rejection-modal-confirm"
                        >
                            {pending && <MicroSpinner className="size-3 animate-spin" />}
                            {/* BVA §4.5: confirm button label */}
                            Reject template
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

// ── ArchiveConfirmTooltip ─────────────────────────────────────────────────────

interface ArchiveConfirmInlineProps {
    onConfirm: () => void;
    onCancel: () => void;
    pending: boolean;
}

/**
 * BVA §4.5 — inline archive confirmation (no modal; inline warning per spec).
 * "Archive this template? It will be removed from the recommendable surface."
 */
const ArchiveConfirmInline = ({
    onConfirm,
    onCancel,
    pending,
}: ArchiveConfirmInlineProps) => (
    <div
        className="mt-2 p-2 border border-citadel-amber/40 bg-citadel-amber/5 flex items-start justify-between gap-3"
        role="alert"
        data-testid="archive-confirm-inline"
    >
        <p className="text-xs text-citadel-ink-dim leading-relaxed">
            {/* BVA §4.5: archive confirm text */}
            Archive this template? It will be removed from the recommendable surface.
        </p>
        <div className="flex items-center gap-2 shrink-0">
            <button
                type="button"
                onClick={onCancel}
                className="px-2 py-1 text-2xs font-mono text-citadel-ink-dim border border-citadel-border hover:text-citadel-ink"
                data-testid="archive-confirm-cancel"
            >
                Cancel
            </button>
            <button
                type="button"
                onClick={onConfirm}
                disabled={pending}
                className="px-2 py-1 text-2xs font-mono bg-citadel-danger/10 border border-citadel-danger/40 text-citadel-danger hover:bg-citadel-danger/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
                data-testid="archive-confirm-proceed"
            >
                {pending && <MicroSpinner className="size-2.5 animate-spin" />}
                Archive
            </button>
        </div>
    </div>
);

// ── RowActions ────────────────────────────────────────────────────────────────

interface QueueRowActionsProps {
    template: CadminTemplate;
    action: RowAction;
    onApprove: (id: string) => void;
    onRejectClick: (id: string) => void;
}

/** Queue tab row action buttons: Approve + Reject. */
const QueueRowActions = ({
    template,
    action,
    onApprove,
    onRejectClick,
}: QueueRowActionsProps) => (
    <div className="flex items-center gap-2 mt-2">
        {/* BVA §4.5: approve button */}
        <button
            type="button"
            onClick={() => onApprove(template.id)}
            disabled={action.pending}
            className="px-2.5 py-1 text-2xs font-mono bg-citadel-success/10 border border-citadel-success/40 text-citadel-success hover:bg-citadel-success/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            data-testid={`citadel-templates-approve-${template.id}`}
            aria-label={`Approve template ${template.name}`}
        >
            {action.pending && <MicroSpinner className="size-2.5 animate-spin" />}
            Approve
        </button>

        {/* BVA §4.5: reject button */}
        <button
            type="button"
            onClick={() => onRejectClick(template.id)}
            disabled={action.pending}
            className="px-2.5 py-1 text-2xs font-mono bg-citadel-danger/10 border border-citadel-danger/40 text-citadel-danger hover:bg-citadel-danger/20 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`citadel-templates-reject-${template.id}`}
            aria-label={`Reject template ${template.name}`}
        >
            Reject
        </button>

        {action.error && (
            <span className="text-2xs text-citadel-danger font-mono">{action.error}</span>
        )}
    </div>
);

interface PublishedRowActionsProps {
    template: CadminTemplate;
    action: RowAction;
    archiveConfirmId: string | null;
    onArchiveClick: (id: string) => void;
    onArchiveConfirm: (id: string) => void;
    onArchiveCancel: () => void;
}

/** Published tab row: Archive action with inline confirm. */
const PublishedRowActions = ({
    template,
    action,
    archiveConfirmId,
    onArchiveClick,
    onArchiveConfirm,
    onArchiveCancel,
}: PublishedRowActionsProps) => (
    <div className="mt-2">
        {archiveConfirmId !== template.id ? (
            <div className="flex items-center gap-2">
                {/* BVA §4.5: archive button */}
                <button
                    type="button"
                    onClick={() => onArchiveClick(template.id)}
                    disabled={action.pending}
                    className="px-2.5 py-1 text-2xs font-mono bg-citadel-border text-citadel-ink-dim border border-citadel-border-strong hover:text-citadel-ink disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`citadel-templates-archive-${template.id}`}
                    aria-label={`Archive template ${template.name}`}
                >
                    Archive
                </button>
                {action.error && (
                    <span className="text-2xs text-citadel-danger font-mono">{action.error}</span>
                )}
            </div>
        ) : (
            <ArchiveConfirmInline
                onConfirm={() => onArchiveConfirm(template.id)}
                onCancel={onArchiveCancel}
                pending={action.pending}
            />
        )}
    </div>
);

// ── TemplateRow ───────────────────────────────────────────────────────────────

interface TemplateRowProps {
    template: CadminTemplate;
    action: RowAction;
    tab: Tab;
    archiveConfirmId: string | null;
    onApprove: (id: string) => void;
    onRejectClick: (id: string) => void;
    onArchiveClick: (id: string) => void;
    onArchiveConfirm: (id: string) => void;
    onArchiveCancel: () => void;
}

const TemplateRow = ({
    template,
    action,
    tab,
    archiveConfirmId,
    onApprove,
    onRejectClick,
    onArchiveClick,
    onArchiveConfirm,
    onArchiveCancel,
}: TemplateRowProps) => (
    <li
        className="border border-citadel-border bg-citadel-surface p-3"
        data-testid={`template-row-${template.id}`}
    >
        {/* Row header: id + origin + name + age */}
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <OriginBadge origin={template.origin} />
                    <span className="text-sm font-semibold text-citadel-ink truncate">
                        {template.name}
                    </span>
                    {template.status === 'draft' && template.origin === 'noeura' && (
                        /* BVA §4.5: auto-approved label (Phase 1 scaffold indicator) */
                        <span className="text-2xs font-mono px-1.5 py-0.5 bg-citadel-cyan/5 text-citadel-ink-faint border border-citadel-border">
                            Auto-approved
                        </span>
                    )}
                </div>
                <p className="mt-0.5 text-2xs font-mono text-citadel-ink-faint truncate">
                    {template.id.slice(0, 16)}{template.id.length > 16 ? '…' : ''}
                    {' · '}
                    {template.tier_minimum}
                </p>
                {template.description && (
                    <p className="mt-1 text-xs text-citadel-ink-dim leading-relaxed line-clamp-2">
                        {template.description}
                    </p>
                )}
            </div>
            <div className="shrink-0 text-2xs font-mono text-citadel-ink-faint">
                {relativeAge(template.created_at)}
            </div>
        </div>

        {/* Actions per tab */}
        {tab === 'queue' && (
            <QueueRowActions
                template={template}
                action={action}
                onApprove={onApprove}
                onRejectClick={onRejectClick}
            />
        )}
        {tab === 'published' && (
            <PublishedRowActions
                template={template}
                action={action}
                archiveConfirmId={archiveConfirmId}
                onArchiveClick={onArchiveClick}
                onArchiveConfirm={onArchiveConfirm}
                onArchiveCancel={onArchiveCancel}
            />
        )}
        {/* Archived tab is read-only — no actions */}
    </li>
);

// ── Toast system ──────────────────────────────────────────────────────────────

interface Toast {
    id: string;
    message: string;
    kind: 'success' | 'error';
}

function useToasts() {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const push = useCallback((message: string, kind: Toast['kind']) => {
        const id = `${Date.now()}-${Math.random()}`;
        setToasts((prev) => [...prev, { id, message, kind }]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 4000);
    }, []);
    return { toasts, push };
}

// ── Main component ────────────────────────────────────────────────────────────

type LoadState = 'loading' | 'ready' | 'error' | 'access-denied';

export default function TemplatesModeration() {
    // BL-29-2163: `ready` flips true only once BOTH the Auth0 bearer token
    // and the tenant id are resolved. The initial fetches are gated on it
    // so the first /v1/citadel/templates/* calls never race the async
    // token+tenant fetch (which would 401 tokenless / 400 tenantless).
    const { ready } = useAccessTokenSync();

    const [activeTab, setActiveTab] = useState<Tab>('queue');

    // Per-tab data + cursor state.
    const [queueItems, setQueueItems] = useState<CadminTemplate[]>([]);
    const [publishedItems, setPublishedItems] = useState<CadminTemplate[]>([]);
    const [archivedItems, setArchivedItems] = useState<CadminTemplate[]>([]);

    const [queueLoadState, setQueueLoadState] = useState<LoadState>('loading');
    const [publishedLoadState, setPublishedLoadState] = useState<LoadState>('loading');
    const [archivedLoadState, setArchivedLoadState] = useState<LoadState>('loading');

    const [queueError, setQueueError] = useState<string | null>(null);
    const [publishedError, setPublishedError] = useState<string | null>(null);
    // archivedError is Phase 2 — the Archived tab is a placeholder (BL-29-1834).
    // Pass null directly in the renderLoadState call below; no state needed yet.

    // Per-row action state: { [templateId]: RowAction }
    const [rowActions, setRowActions] = useState<Record<string, RowAction>>({});

    // Archive inline confirm state.
    const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);

    // Rejection modal state.
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectTargetId, setRejectTargetId] = useState<string | null>(null);
    const [rejectPending, setRejectPending] = useState(false);

    const { toasts, push: pushToast } = useToasts();

    const mountedRef = useRef(true);
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // ── Data fetchers ─────────────────────────────────────────────────────────

    const fetchQueue = useCallback(async () => {
        setQueueLoadState('loading');
        setQueueError(null);
        try {
            const res = await listModerationQueue();
            if (!mountedRef.current) return;
            setQueueItems(res.items ?? []);
            setQueueLoadState('ready');
        } catch (err) {
            if (!mountedRef.current) return;
            if (err instanceof CitadelApiError && err.status === 403) {
                setQueueLoadState('access-denied');
                return;
            }
            setQueueError(err instanceof Error ? err.message : 'Failed to load moderation queue.');
            setQueueLoadState('error');
        }
    }, []);

    const fetchPublished = useCallback(async () => {
        setPublishedLoadState('loading');
        setPublishedError(null);
        try {
            const res = await listPublished();
            if (!mountedRef.current) return;
            setPublishedItems(res.items ?? []);
            setPublishedLoadState('ready');
        } catch (err) {
            if (!mountedRef.current) return;
            if (err instanceof CitadelApiError && err.status === 403) {
                setPublishedLoadState('access-denied');
                return;
            }
            setPublishedError(err instanceof Error ? err.message : 'Failed to load published templates.');
            setPublishedLoadState('error');
        }
    }, []);

    // Archived tab reuses the published endpoint filtered by status — the
    // backend returns archived items from the same UNION view. We fetch
    // them via a dedicated archived endpoint that will be added in Phase 2;
    // for Phase 1 scaffold we present a read-only view from what the
    // moderation queue moves to archived state, relying on the published
    // endpoint's full UNION view until the dedicated archived endpoint lands.
    // NOTE: The BE does not yet expose GET /v1/citadel/templates/archived —
    // this is filed as BL-29-1834 Phase 2. For Phase 1 we show a
    // placeholder "coming in Phase 2" state on the Archived tab.
    const [archivedPhase2Pending] = useState(true);

    // Fetch on mount — queue and published load once the API is ready;
    // archived is a Phase 2 placeholder. The fetches are gated on `ready`
    // so they do not race the async Auth0 token+tenant resolution
    // (BL-29-2163). Until ready, queue + published stay in their default
    // `loading` state — the error state is reserved for genuine failures.
    useEffect(() => {
        if (!ready) return;
        void fetchQueue();
        void fetchPublished();
        setArchivedLoadState('ready');
        setArchivedItems([]);
    }, [ready, fetchQueue, fetchPublished]);

    // ── Action handlers ───────────────────────────────────────────────────────

    const setRowPending = (id: string, pending: boolean, error: string | null = null) => {
        setRowActions((prev) => ({ ...prev, [id]: { pending, error } }));
    };

    const removeFromQueue = (id: string) => {
        setQueueItems((prev) => prev.filter((t) => t.id !== id));
    };

    const handleApprove = useCallback(async (id: string) => {
        setRowPending(id, true);
        try {
            await approveTemplate(id);
            if (!mountedRef.current) return;
            removeFromQueue(id);
            // Refresh published list so the newly approved template appears.
            void fetchPublished();
            // BVA §4.5: approve success toast
            pushToast('Template approved and published.', 'success');
            setRowPending(id, false);
        } catch (err) {
            if (!mountedRef.current) return;
            const msg = err instanceof Error ? err.message : 'Approve failed.';
            setRowPending(id, false, msg);
        }
    }, [fetchPublished, pushToast]);

    const handleRejectClick = useCallback((id: string) => {
        setRejectTargetId(id);
        setRejectModalOpen(true);
    }, []);

    const handleRejectConfirm = useCallback(async (reason: string) => {
        if (!rejectTargetId) return;
        const id = rejectTargetId;
        setRejectPending(true);
        try {
            await rejectTemplate(id, reason);
            if (!mountedRef.current) return;
            setRejectModalOpen(false);
            setRejectTargetId(null);
            removeFromQueue(id);
            // BVA §4.5: reject success toast
            pushToast('Template rejected.', 'success');
            setRowPending(id, false);
        } catch (err) {
            if (!mountedRef.current) return;
            const msg = err instanceof Error ? err.message : 'Reject failed.';
            setRowPending(id, false, msg);
            pushToast(msg, 'error');
        } finally {
            if (mountedRef.current) setRejectPending(false);
        }
    }, [rejectTargetId, pushToast]);

    const handleArchiveClick = useCallback((id: string) => {
        setArchiveConfirmId(id);
    }, []);

    const handleArchiveCancel = useCallback(() => {
        setArchiveConfirmId(null);
    }, []);

    const handleArchiveConfirm = useCallback(async (id: string) => {
        setRowPending(id, true);
        // Archive is a PATCH status transition — the BE exposes it via
        // PATCH /v1/templates/{id} (status: 'archived') for published rows.
        // In Phase 1 scaffold, Citadel-side archive is not yet wired to a
        // dedicated endpoint (that lands in BL-29-1834 Phase 2). For now,
        // we surface the same reject endpoint with a "Archived by admin" reason.
        // This is explicitly documented as Phase 1 scaffold behaviour.
        try {
            await rejectTemplate(id, 'Archived by Citadel admin.');
            if (!mountedRef.current) return;
            setPublishedItems((prev) => prev.filter((t) => t.id !== id));
            setArchiveConfirmId(null);
            // BVA §4.5: archive success toast
            pushToast('Template archived.', 'success');
            setRowPending(id, false);
        } catch (err) {
            if (!mountedRef.current) return;
            const msg = err instanceof Error ? err.message : 'Archive failed.';
            setRowPending(id, false, msg);
            setArchiveConfirmId(null);
        }
    }, [pushToast]);

    // ── Tab content renderers ─────────────────────────────────────────────────

    const renderLoadState = (
        loadState: LoadState,
        error: string | null,
        onRetry: () => void,
        emptyMsg: string,
        items: CadminTemplate[],
        tabKey: Tab,
    ) => {
        // NEW-P0-A binding: 403 from BE renders clean Access-denied state.
        if (loadState === 'access-denied') {
            return (
                <div
                    className="flex items-start gap-3 border border-citadel-danger/40 bg-citadel-danger/5 p-4"
                    role="alert"
                    data-testid="templates-access-denied"
                >
                    <ShieldAlert className="size-4 text-citadel-danger mt-0.5 shrink-0" weight="thin" />
                    <div className="text-xs">
                        <p className="font-semibold text-citadel-danger">
                            Access denied — operations role required
                        </p>
                        <p className="mt-1 text-citadel-ink-dim">
                            Your account does not hold the{' '}
                            <code className="font-mono text-citadel-ink">operations:read</code>{' '}
                            permission. Contact your platform administrator.
                        </p>
                    </div>
                </div>
            );
        }

        if (loadState === 'loading') {
            return (
                <div
                    role="status"
                    className="flex items-center gap-2 border border-citadel-border bg-citadel-surface p-3 text-xs font-mono text-citadel-ink-dim"
                >
                    <MicroSpinner className="size-3.5 animate-spin" />
                    Loading…
                </div>
            );
        }

        if (loadState === 'error') {
            return (
                <div
                    role="alert"
                    className="flex items-start gap-2 border border-citadel-danger/40 bg-citadel-danger/10 p-3"
                >
                    <AlertTriangle className="size-4 text-citadel-danger mt-0.5 shrink-0" weight="thin" />
                    <div className="text-xs">
                        <p className="font-semibold text-citadel-danger">Failed to load.</p>
                        <p className="mt-1 text-citadel-ink-dim">{error}</p>
                        <button
                            type="button"
                            onClick={onRetry}
                            className="mt-2 text-2xs font-mono uppercase tracking-wider px-2 py-1 border border-citadel-danger/40 text-citadel-danger hover:bg-citadel-danger/10"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            );
        }

        // Archived tab Phase 2 placeholder.
        if (tabKey === 'archived' && archivedPhase2Pending) {
            return (
                <div
                    className="border border-citadel-border bg-citadel-surface p-4 text-xs text-citadel-ink-dim font-mono"
                    data-testid="templates-archived-phase2-placeholder"
                >
                    Archived templates surface coming in Phase 2 (BL-29-1834).
                    Rejected and archived rows are visible in the OPAL audit log.
                </div>
            );
        }

        if (items.length === 0) {
            return (
                <div
                    className="border border-citadel-border bg-citadel-surface p-4 text-xs text-citadel-ink-dim font-mono"
                    data-testid={`templates-empty-${tabKey}`}
                >
                    {/* BVA §4.5: empty state messages */}
                    {emptyMsg}
                </div>
            );
        }

        return (
            <ul className="space-y-1.5" data-testid={`templates-list-${tabKey}`}>
                {items.map((t) => (
                    <TemplateRow
                        key={t.id}
                        template={t}
                        action={rowActions[t.id] ?? { pending: false, error: null }}
                        tab={tabKey}
                        archiveConfirmId={archiveConfirmId}
                        onApprove={handleApprove}
                        onRejectClick={handleRejectClick}
                        onArchiveClick={handleArchiveClick}
                        onArchiveConfirm={handleArchiveConfirm}
                        onArchiveCancel={handleArchiveCancel}
                    />
                ))}
            </ul>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <section
            className="space-y-4"
            aria-labelledby="templates-moderation-heading"
            data-testid="templates-moderation"
        >
            {/* BVA §4.5: page heading + subtext */}
            <header className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <ClipboardList className="size-4 text-citadel-cyan" weight="thin" aria-hidden />
                        <h1
                            id="templates-moderation-heading"
                            className="text-lg font-semibold tracking-tight"
                        >
                            Templates Moderation
                        </h1>
                    </div>
                    <p className="mt-1 text-xs text-citadel-ink-dim leading-relaxed max-w-2xl">
                        Review and action Noeura-generated and user-submitted templates
                        before they reach the recommendable surface.
                    </p>
                </div>
                <span className="text-2xs font-mono text-citadel-ink-faint shrink-0">
                    /v1/citadel/templates/*
                </span>
            </header>

            {/* BVA §4.5: queue count summary */}
            {queueLoadState === 'ready' && queueItems.length > 0 && (
                <p
                    className="text-xs font-mono text-citadel-ink-dim"
                    data-testid="templates-queue-count"
                    aria-live="polite"
                >
                    {queueItems.length} template{queueItems.length !== 1 ? 's' : ''} pending review
                </p>
            )}

            {/* Tab bar */}
            <div
                className="flex items-center gap-1 border-b border-citadel-border"
                role="tablist"
                aria-label="Template moderation tabs"
            >
                {(
                    [
                        { key: 'queue', label: 'Queue', testid: 'citadel-templates-tab-queue' },
                        { key: 'published', label: 'Published', testid: 'citadel-templates-tab-published' },
                        { key: 'archived', label: 'Archived', testid: 'citadel-templates-tab-archived' },
                    ] as const
                ).map(({ key, label, testid }) => (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === key}
                        aria-controls={`tab-panel-${key}`}
                        id={`tab-${key}`}
                        onClick={() => setActiveTab(key)}
                        data-testid={testid}
                        className={`px-3 py-2 text-xs font-mono transition-colors -mb-px border-b-2 ${
                            activeTab === key
                                ? 'border-citadel-cyan text-citadel-cyan'
                                : 'border-transparent text-citadel-ink-dim hover:text-citadel-ink'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {/* Queue tab panel */}
            <div
                id="tab-panel-queue"
                role="tabpanel"
                aria-labelledby="tab-queue"
                hidden={activeTab !== 'queue'}
                data-testid="tab-panel-queue"
            >
                {renderLoadState(
                    queueLoadState,
                    queueError,
                    fetchQueue,
                    /* BVA §4.5 */ 'No templates pending review.',
                    queueItems,
                    'queue',
                )}
            </div>

            {/* Published tab panel */}
            <div
                id="tab-panel-published"
                role="tabpanel"
                aria-labelledby="tab-published"
                hidden={activeTab !== 'published'}
                data-testid="tab-panel-published"
            >
                {renderLoadState(
                    publishedLoadState,
                    publishedError,
                    fetchPublished,
                    /* BVA §4.5 */ 'No published templates.',
                    publishedItems,
                    'published',
                )}
            </div>

            {/* Archived tab panel */}
            <div
                id="tab-panel-archived"
                role="tabpanel"
                aria-labelledby="tab-archived"
                hidden={activeTab !== 'archived'}
                data-testid="tab-panel-archived"
            >
                {renderLoadState(
                    archivedLoadState,
                    null, // Phase 1 placeholder — no error state wired for archived tab
                    () => setArchivedLoadState('ready'),
                    /* BVA §4.5 */ 'No archived templates.',
                    archivedItems,
                    'archived',
                )}
            </div>

            {/* Rejection reason modal */}
            <RejectionReasonModal
                open={rejectModalOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setRejectModalOpen(false);
                        setRejectTargetId(null);
                    }
                }}
                onConfirm={handleRejectConfirm}
                pending={rejectPending}
            />

            {/* Toast container */}
            {toasts.length > 0 && (
                <div
                    className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
                    aria-live="polite"
                    data-testid="toast-container"
                >
                    {toasts.map((toast) => (
                        <div
                            key={toast.id}
                            className={`flex items-center gap-2 px-3 py-2 border text-xs font-mono shadow-md ${
                                toast.kind === 'success'
                                    ? 'border-citadel-success/40 bg-citadel-surface text-citadel-success'
                                    : 'border-citadel-danger/40 bg-citadel-surface text-citadel-danger'
                            }`}
                            data-testid={`toast-${toast.kind}`}
                        >
                            {toast.kind === 'success' ? (
                                <MicroCheckCircle className="size-3 shrink-0" />
                            ) : (
                                <MicroAlertTriangle className="size-3 shrink-0" />
                            )}
                            {toast.message}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}
